import React from 'react';
import { Target, Plus } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Componente per visualizzare badge dei muscoli (primario e secondari)
 * 
 * @param {object} primaryMuscle - Muscolo primario {id, name, category}
 * @param {Array} secondaryMuscles - Array di muscoli secondari [{id, name, activation_level}]
 * @param {string} variant - Variante di visualizzazione: 'compact' | 'expanded' | 'minimal'
 * @param {boolean} showCategory - Se true, mostra la categoria del muscolo primario
 * @param {string} className - Classi CSS aggiuntive
 */
const MuscleBadges = ({ 
  primaryMuscle, 
  secondaryMuscles = [], 
  variant = 'compact',
  showCategory = false,
  className = '' 
}) => {
  const { isDark } = useTheme();

  // Se non c'è muscolo primario, non mostrare nulla
  if (!primaryMuscle) {
    return null;
  }

  /**
   * Ottiene le classi CSS per il badge in base al livello di attivazione
   */
  const getActivationClasses = (activationLevel) => {
    const baseClasses = 'inline-flex items-center px-2 py-1 rounded text-xs font-medium transition-colors';
    
    switch (activationLevel) {
      case 'high':
        return `${baseClasses} ${
          isDark 
            ? 'bg-blue-900 text-blue-200 border border-blue-700' 
            : 'bg-blue-100 text-blue-800 border border-blue-300'
        }`;
      case 'medium':
        return `${baseClasses} ${
          isDark 
            ? 'bg-green-900 text-green-200 border border-green-700' 
            : 'bg-green-100 text-green-800 border border-green-300'
        }`;
      case 'low':
        return `${baseClasses} ${
          isDark 
            ? 'bg-gray-700 text-gray-300 border border-gray-600' 
            : 'bg-gray-100 text-gray-700 border border-gray-300'
        }`;
      default:
        return `${baseClasses} ${
          isDark 
            ? 'bg-gray-700 text-gray-300 border border-gray-600' 
            : 'bg-gray-100 text-gray-700 border border-gray-300'
        }`;
    }
  };

  /**
   * Classi per il badge primario
   */
  const primaryClasses = `inline-flex items-center px-3 py-1 rounded-md text-sm font-semibold transition-colors ${
    isDark 
      ? 'bg-indigo-900 text-indigo-100 border-2 border-indigo-600' 
      : 'bg-indigo-100 text-indigo-900 border-2 border-indigo-400'
  }`;

  /**
   * Renderizza la versione MINIMAL (solo muscolo primario, molto compatto)
   */
  if (variant === 'minimal') {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <Target className="h-3 w-3 text-indigo-600 dark:text-indigo-400" />
        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
          {primaryMuscle.name}
        </span>
      </div>
    );
  }

  /**
   * Renderizza la versione COMPACT (primario + secondari in linea)
   */
  if (variant === 'compact') {
    return (
      <div className={`flex flex-wrap items-center gap-2 ${className}`}>
        {/* Badge primario */}
        <span className={primaryClasses}>
          <Target className="h-3 w-3 mr-1" />
          {primaryMuscle.name}
          {showCategory && primaryMuscle.category && (
            <span className="ml-1 opacity-75 text-xs">({primaryMuscle.category})</span>
          )}
        </span>

        {/* Badge secondari */}
        {secondaryMuscles && secondaryMuscles.length > 0 && (
          <>
            {secondaryMuscles.map((muscle, index) => (
              <span 
                key={muscle.id || index} 
                className={getActivationClasses(muscle.activation_level)}
              >
                <Plus className="h-3 w-3 mr-1" />
                {muscle.name}
              </span>
            ))}
          </>
        )}
      </div>
    );
  }

  /**
   * Renderizza la versione EXPANDED (primario sopra, secondari sotto con dettagli)
   */
  if (variant === 'expanded') {
    return (
      <div className={`space-y-3 ${className}`}>
        {/* Muscolo primario */}
        <div>
          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
            Muscolo Primario
          </div>
          <span className={primaryClasses}>
            <Target className="h-4 w-4 mr-2" />
            <span>
              {primaryMuscle.name}
              {showCategory && primaryMuscle.category && (
                <span className="ml-2 opacity-75 text-xs">• {primaryMuscle.category}</span>
              )}
            </span>
          </span>
        </div>

        {/* Muscoli secondari */}
        {secondaryMuscles && secondaryMuscles.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Muscoli Secondari
            </div>
            <div className="flex flex-wrap gap-2">
              {secondaryMuscles.map((muscle, index) => (
                <span 
                  key={muscle.id || index} 
                  className={getActivationClasses(muscle.activation_level)}
                  title={`Attivazione: ${muscle.activation_level}`}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {muscle.name}
                  <span className="ml-1 opacity-60">
                    ({muscle.activation_level === 'high' ? 'Alta' : 
                      muscle.activation_level === 'medium' ? 'Media' : 'Bassa'})
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Default: compact
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <span className={primaryClasses}>
        <Target className="h-3 w-3 mr-1" />
        {primaryMuscle.name}
      </span>
      {secondaryMuscles && secondaryMuscles.length > 0 && (
        <>
          {secondaryMuscles.map((muscle, index) => (
            <span 
              key={muscle.id || index} 
              className={getActivationClasses(muscle.activation_level)}
            >
              <Plus className="h-3 w-3 mr-1" />
              {muscle.name}
            </span>
          ))}
        </>
      )}
    </div>
  );
};

export default MuscleBadges;


